import type { LucideIcon } from 'lucide-react';
import {
  Video, Mail, Shield, Cloud, Users, ArrowRightLeft,
  Monitor, Puzzle, Server, Network, Database, GitMerge,
  Sparkles, Cpu, Boxes, Share2, Repeat, UserCog, BadgeCheck,
} from 'lucide-react';

export type DeploymentModel = 'sovereign-cloud' | 'on-premise' | 'standalone' | 'saas';

export interface ProductFeature {
  title: string;
  description: string;
}

export interface ProductStat {
  value: string;
  label: string;
}

export interface ProductMeta {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  accent: string; // tailwind gradient classes
  category: 'ai' | 'collaboration' | 'identity' | 'infrastructure' | 'platform' | 'migration';
  badge?: string;
  deployments: DeploymentModel[];
  features: ProductFeature[];
  stats: ProductStat[];
  highlights: string[];
  pricingNote: string;
}

export const products: ProductMeta[] = [
  {
    slug: 'ai-infrastructure',
    name: 'AI Infrastructure',
    tagline: 'GPU passthrough, model serving and RAG pipelines on Proxmox VE',
    description:
      'Deploy and operate AI workloads on your own Proxmox VE infrastructure. GPU passthrough, vLLM and TGI model serving, Qdrant/ChromaDB vector databases, RAG pipelines with LangChain — all on-premise, nothing in the cloud.',
    icon: Cpu,
    accent: 'from-blue-600 to-cyan-500',
    category: 'ai',
    badge: 'NEW',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'GPU passthrough on Proxmox VE', description: 'Dedicated GPU access for VMs — run LLM inference at full speed.' },
      { title: 'Model serving with vLLM & TGI', description: 'Production-grade LLM serving with OpenAI-compatible API — on your GPUs.' },
      { title: 'RAG pipelines', description: 'Qdrant + ChromaDB + LangChain — build retrieval-augmented AI on your data.' },
      { title: 'AI monitoring', description: 'Prometheus + Grafana dashboards for GPU utilization, inference latency, token throughput.' },
      { title: 'On-premise only', description: 'No data leaves your servers. No OpenAI, no cloud API calls, no black boxes.' },
      { title: 'Docker Compose deploy', description: 'Full AI stack in one command — Ollama, vLLM, Qdrant, OpenWebUI, LiteLLM.' },
    ],
    stats: [
      { value: 'GPU', label: 'Passthrough' },
      { value: 'vLLM', label: 'Model serving' },
      { value: 'RAG', label: 'Pipelines' },
      { value: 'On-prem', label: 'Only' },
    ],
    highlights: [
      'GPU passthrough on Proxmox VE — full-speed LLM inference',
      'RAG pipelines with Qdrant + LangChain on your data',
      'Production model serving with vLLM and TGI',
      'On-premise only — nothing leaves your servers',
    ],
    pricingNote: 'Per-instance licensing. GPU hardware not included. Contact for quote.',
  },
  {
    slug: 'ai-open-source-stack',
    name: 'Open-Source AI Stack',
    tagline: 'Complete AI stack on your infrastructure — Ollama, Qdrant, Whisper, Stable Diffusion',
    description:
      'A complete open-source AI stack running entirely on your servers. Local LLMs with Ollama and llama.cpp, vector databases with Qdrant, speech-to-text with Whisper, image generation with Stable Diffusion, and an AI gateway with LiteLLM and OpenWebUI. Nothing in the cloud, nothing to OpenAI.',
    icon: Boxes,
    accent: 'from-cyan-500 to-blue-600',
    category: 'ai',
    badge: 'NEW',
    deployments: ['on-premise', 'standalone'],
    features: [
      { title: 'Ollama & llama.cpp', description: 'Run Llama, Mistral, Qwen and 50+ open models locally — quantized for your GPU.' },
      { title: 'Vector databases', description: 'Qdrant, ChromaDB and pgvector — semantic search over your documents and data.' },
      { title: 'Whisper & Piper', description: 'On-premise speech-to-text and text-to-speech — transcribe meetings, generate audio.' },
      { title: 'Stable Diffusion', description: 'On-premise image generation — no DALL-E, no Midjourney, your GPUs.' },
      { title: 'AI gateway', description: 'LiteLLM + OpenWebUI — unified API for all your models, OpenAI-compatible.' },
      { title: 'Docker Compose', description: 'Full stack in one command. Every service containerized, reproducible, auditable.' },
    ],
    stats: [
      { value: '50+', label: 'Open models' },
      { value: 'RAG', label: 'Built-in' },
      { value: 'On-prem', label: 'Only' },
      { value: 'Open', label: 'Source' },
    ],
    highlights: [
      'Complete open-source AI stack — Ollama, Qdrant, Whisper, Stable Diffusion',
      '50+ open models — Llama, Mistral, Qwen — on your GPUs',
      'AI gateway with LiteLLM + OpenWebUI — OpenAI-compatible API',
      'Docker Compose — full stack in one command',
    ],
    pricingNote: 'Per-instance licensing. Open-source stack is free to self-host. Managed plan available — contact for quote.',
  },
  {
    slug: 'zimbra-sovereign-ai',
    name: 'Zimbra Sovereign AI',
    tagline: 'Zimbra + on-premise AI on a European sovereign cloud',
    description:
      'Zimbra Collaboration Suite supercharged with on-premise AI — Whisper transcription, LLM summaries, semantic search and an AI assistant — running on your GPUs in a sovereign European cloud. No CLOUD Act, no data to OpenAI or Google, GDPR-native.',
    icon: Sparkles,
    accent: 'from-sky-400 via-blue-600 to-cyan-500',
    category: 'collaboration',
    badge: 'Flagship',
    deployments: ['sovereign-cloud', 'on-premise'],
    features: [
      { title: 'AI email triage', description: 'LLM classifies, prioritizes and summarizes your inbox — drafts replies in your tone, on your GPU.' },
      { title: 'Whisper transcription', description: 'Voicemail and meeting recordings transcribed in real time — searchable, archivable, on-premise.' },
      { title: 'Semantic search', description: 'Search across emails, calendars, files and chats with natural language — embeddings stay on your servers.' },
      { title: 'AI Assistant in webmail', description: 'A sovereign assistant inside Modern & Classic webmail — schedule, draft, query, summarize — zero setup via zimlet.' },
      { title: 'Sovereign European cloud', description: 'Managed by ilger.com in EU datacenters — no CLOUD Act, no US provider, GDPR-native.' },
      { title: 'On-premise option', description: 'Same stack runs on your own servers — air-gapped if you want. Two servers, one command.' },
      { title: 'Native Zimbra integration', description: 'A zimlet drops AI into Zimbra webmail — toolbar icon, transparent SSO, zero setup for users.' },
      { title: 'Open models', description: 'Whisper, Llama, Mistral — open-weight models you can audit, fine-tune and own. No black boxes.' },
    ],
    stats: [
      { value: 'AI', label: 'On-premise only' },
      { value: 'EU', label: 'Sovereign cloud' },
      { value: '0', label: 'CLOUD Act exposure' },
      { value: 'GDPR', label: 'Native' },
    ],
    highlights: [
      'Zimbra + on-premise AI — nothing leaves your servers',
      'Sovereign European cloud — managed by ilger.com',
      'AI assistant native in Zimbra webmail — zero setup',
      'Open models — Whisper, Llama, Mistral — auditable',
    ],
    pricingNote: 'Per-instance licensing. Unlimited mailboxes, unlimited AI calls. Sovereign-cloud managed plan available — contact for quote.',
  },
  {
    slug: 'next',
    name: 'NEXT Solution',
    tagline: 'Sovereign collaboration suite for Zimbra',
    description:
      'HD video, modern chat, calendar and AI — grafted natively onto Zimbra. Run it as a managed European cloud by ilger.com, or on-premise. Nothing crosses the Atlantic, no CLOUD Act, GDPR-native.',
    icon: Video,
    accent: 'from-cyan-500 to-blue-600',
    category: 'collaboration',
    badge: 'Flagship',
    deployments: ['sovereign-cloud', 'on-premise', 'standalone'],
    features: [
      { title: 'HD Video', description: 'LiveKit SFU, 80+ participants, adaptive simulcast.' },
      { title: 'Teams-style chat', description: 'Threads, mentions, reactions, attachments, presence.' },
      { title: 'Zimbra Calendar', description: 'Day/Week/Month views, auto-generated meeting links.' },
      { title: 'Files', description: 'Zimbra Briefcase + aggregated chat attachments.' },
      { title: 'AI Summary', description: 'Whisper transcription + on-prem LLM summary.' },
      { title: 'Native mobile', description: 'Android v2.3.8 signed + iOS in progress.' },
      { title: 'Native Zimbra zimlet', description: 'A zimlet drops NEXT into Modern & Classic webmail — a toolbar icon, "Start a meeting" in one click, transparent SSO. Zero setup for users.' },
      { title: 'On-premise AI', description: 'Copilot sends your conversations to OpenAI. Google sends to Gemini. NEXT keeps everything in-house — Whisper transcription and LLM summaries run on your own GPU.' },
    ],
    stats: [
      { value: '6', label: 'Products · One platform' },
      { value: '80+', label: 'Participants / room' },
      { value: 'EU', label: 'Sovereign cloud' },
      { value: '0', label: 'CLOUD Act exposure' },
    ],
    highlights: [
      'Sovereign European cloud — managed by ilger.com',
      'On-premise AI — nothing leaves your servers',
      'Native Zimbra zimlet — zero setup for users',
      'Per-instance licensing — unlimited users',
    ],
    pricingNote: 'Per-instance licensing. Unlimited users, unlimited rooms. Contact for sovereign-cloud managed plan.',
  },
  {
    slug: 'zimbra-admin-panel',
    name: 'Zimbra Admin Panel',
    tagline: 'Multi-tenant BSP control plane for Zimbra',
    description:
      'The complete toolset for Zimbra BSPs — manage multi-tenant infrastructure, customers, domains, COS, health monitoring and auto-sync. Connect new Zimbra servers in seconds via SOAP API.',
    icon: Monitor,
    accent: 'from-slate-500 to-blue-700',
    category: 'platform',
    badge: 'BSP tool',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Multi-tenant dashboard', description: 'Full infrastructure overview — customers, instances, mail accounts.' },
      { title: 'Server subscription', description: 'Connect a new Zimbra server in seconds via SOAP API.' },
      { title: 'Mailbox sharing', description: 'Grant and revoke mailbox access across accounts.' },
      { title: 'License tracking', description: 'Track product subscriptions, quotas and Zimlet licenses.' },
      { title: 'Signature Center', description: 'Deploy branded HTML email signatures to all accounts.' },
      { title: 'Health monitoring', description: 'Real-time health checks and auto-sync across nodes.' },
    ],
    stats: [
      { value: 'SOAP', label: 'API integration' },
      { value: '∞', label: 'Tenants managed' },
      { value: '5', label: 'Languages' },
      { value: '24/7', label: 'Monitoring' },
    ],
    highlights: [
      'Multi-tenant BSP control plane',
      'Connect Zimbra servers via SOAP API',
      'Branded signature deployment',
      'Real-time health monitoring',
    ],
    pricingNote: 'Per-instance licensing for BSPs. Tiered by managed tenant count. Contact for quote.',
  },
  {
    slug: 'scalenix-os',
    name: 'ScaleNix OS',
    tagline: 'Sovereign cloud web desktop built on NixOS',
    description:
      'A full-featured web desktop environment with 30+ integrated apps. Email, files, chat, video, documents — unified behind a single sign-on, running entirely in the browser. Open source, LGPL-2.0.',
    icon: Monitor,
    accent: 'from-blue-500 to-cyan-400',
    category: 'platform',
    badge: 'Open Source',
    deployments: ['on-premise', 'standalone'],
    features: [
      { title: 'Window Manager', description: 'Drag, resize, snap, minimize, maximize, Alt+Tab, Picture-in-Picture — a real desktop experience in the browser.' },
      { title: 'Single Sign-On', description: 'Keycloak OIDC with PKCE, role-based access control: user, developer, admin.' },
      { title: '5 Languages', description: 'French, English, Italian, Spanish, German. 560+ translation keys per language.' },
      { title: 'Themes & Widgets', description: '7 themes, 15 desktop widgets, custom wallpapers and accent colors.' },
      { title: 'Spotlight Search', description: 'Press Ctrl+K to instantly find and launch any app, file or setting.' },
      { title: 'Docker Compose', description: 'Full stack in one command. Traefik v3 reverse proxy, TLS termination, 20+ services.' },
    ],
    stats: [
      { value: '30+', label: 'Applications' },
      { value: '5', label: 'Languages' },
      { value: '15', label: 'Desktop Widgets' },
      { value: '20+', label: 'Docker Services' },
    ],
    highlights: [
      '30+ integrated apps, single sign-on',
      'Open source — LGPL-2.0',
      'Docker Compose — one command deploy',
      '7 themes, 5 languages',
    ],
    pricingNote: 'Open source (LGPL-2.0). Self-host free. Managed cloud plan available — contact for quote.',
  },
  {
    slug: 'zimlet-nextcloud',
    name: 'Zimlet Nextcloud NG',
    tagline: 'Nextcloud integration directly inside Zimbra',
    description:
      'Seamless Nextcloud integration for file management inside Zimbra webmail. Users access, share and attach files without leaving their mailbox.',
    icon: Cloud,
    accent: 'from-blue-500 to-sky-700',
    category: 'collaboration',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'In-webmail files', description: 'Browse, upload and share Nextcloud files without leaving Zimbra.' },
      { title: 'Smart attachments', description: 'Attach files from Nextcloud directly into emails.' },
      { title: 'Share links', description: 'Generate Nextcloud share links from inside the compose window.' },
      { title: 'Transparent SSO', description: 'Users authenticated once — no second login to Nextcloud.' },
    ],
    stats: [
      { value: '1-click', label: 'Install zimlet' },
      { value: 'SSO', label: 'Transparent auth' },
      { value: 'EU', label: 'Hosted option' },
      { value: 'GDPR', label: 'Native' },
    ],
    highlights: [
      'Nextcloud files inside Zimbra webmail',
      'Smart attachments from Nextcloud',
      'Transparent SSO — no second login',
      'Share links from compose window',
    ],
    pricingNote: 'Per-instance licensing. Bundled with NEXT suite or standalone. Contact for quote.',
  },
  {
    slug: 'zimlet-next',
    name: 'Zimlet Solution Next',
    tagline: 'The Solution Next chat and video experience inside Zimbra',
    description:
      'The Solution Next zimlet brings unified messaging and multi-platform video conferencing into Zimbra webmail. Users start chats and video calls without leaving their mailbox.',
    icon: Mail,
    accent: 'from-emerald-500 to-teal-600',
    category: 'collaboration',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'In-webmail chat', description: 'Threads, mentions, reactions and presence inside Zimbra.' },
      { title: 'One-click video', description: 'Start a video call from any chat or email thread.' },
      { title: 'Multi-platform', description: 'Works across desktop, mobile and browser.' },
      { title: 'Transparent SSO', description: 'No second login — users authenticated via Zimbra.' },
    ],
    stats: [
      { value: '1-click', label: 'Start a call' },
      { value: 'SSO', label: 'Transparent auth' },
      { value: 'Multi', label: 'Platform' },
      { value: 'GDPR', label: 'Native' },
    ],
    highlights: [
      'Chat and video inside Zimbra',
      'One-click video from any thread',
      'Multi-platform support',
      'Transparent SSO',
    ],
    pricingNote: 'Per-instance licensing. Bundled with NEXT suite or standalone. Contact for quote.',
  },
  {
    slug: 'zimbra-migration',
    name: 'Zimbra Real-time Migration',
    tagline: 'Zero-downtime migration from Zimbra 8.8.15 to 10.1.x',
    description:
      'Zero-downtime migration from Zimbra 8.8.15 to 10.1.x. Real-time mailbox sync, delta reconciliation, rollback safety. Certified Zimbra engineers run the migration with you.',
    icon: ArrowRightLeft,
    accent: 'from-orange-500 to-red-600',
    category: 'migration',
    deployments: ['on-premise'],
    features: [
      { title: 'Real-time sync', description: 'Mailboxes continuously synced during migration — no downtime.' },
      { title: 'Delta reconciliation', description: 'Only changes propagated after initial sync — minimal bandwidth.' },
      { title: 'Rollback safety', description: 'Full rollback path if anything goes wrong mid-migration.' },
      { title: 'Certified engineers', description: 'Migration run by certified Zimbra engineers — not a self-serve tool.' },
    ],
    stats: [
      { value: '0', label: 'Downtime' },
      { value: '8.8.15→10.1', label: 'Upgrade path' },
      { value: '24/7', label: 'Engineer support' },
      { value: '100%', label: 'Mailbox integrity' },
    ],
    highlights: [
      'Zero-downtime real-time sync',
      'Delta reconciliation — minimal bandwidth',
      'Full rollback safety',
      'Certified Zimbra engineers',
    ],
    pricingNote: 'Project-based pricing. Scoped by mailbox count and source version. Contact for quote.',
  },
  {
    slug: 'exchange-to-zimbra',
    name: 'Exchange To Zimbra',
    tagline: 'Leave Microsoft Exchange and Microsoft 365 for sovereign Zimbra',
    description:
      'A complete migration path from Microsoft Exchange (on-premise or Microsoft 365) to Zimbra. Emails, calendars, contacts, tasks and shared folders are migrated with continuous sync, so users switch over in a single evening with nothing lost.',
    icon: Repeat,
    accent: 'from-sky-500 to-blue-700',
    category: 'migration',
    badge: 'NEW',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Full data migration', description: 'Emails, calendars, contacts, tasks and shared mailboxes moved with folder structure intact.' },
      { title: 'Exchange & Microsoft 365', description: 'Works from Exchange 2013/2016/2019 on-premise and from Microsoft 365 tenants.' },
      { title: 'Continuous sync', description: 'Mailboxes stay in sync until cutover day — users keep working on Exchange meanwhile.' },
      { title: 'Identity mapping', description: 'Active Directory accounts, aliases and distribution lists recreated in Zimbra automatically.' },
      { title: 'Outlook continuity', description: 'Users can keep Outlook through Zimbra connectors, or move to the Zimbra webmail.' },
      { title: 'Certified engineers', description: 'Audit, pilot, migration waves and cutover run by certified Zimbra engineers.' },
    ],
    stats: [
      { value: '0', label: 'Data lost' },
      { value: 'M365', label: 'Exchange sources' },
      { value: '1', label: 'Evening cutover' },
      { value: 'EU', label: 'Sovereign target' },
    ],
    highlights: [
      'Exchange & Microsoft 365 to Zimbra',
      'Emails, calendars, contacts and shared folders',
      'Continuous sync until cutover day',
      'Out of the CLOUD Act — into a sovereign platform',
    ],
    pricingNote: 'Project-based pricing. Scoped by mailbox count and data volume. Contact for quote.',
  },
  {
    slug: 'lemonldap',
    name: 'LemonLDAP::NG',
    tagline: 'Web SSO and access management',
    description:
      'Enterprise web SSO and access management. Centralized authentication, federated identity, role-based access control. Integrates with Zimbra, Nextcloud and your existing directory.',
    icon: Shield,
    accent: 'from-yellow-500 to-amber-600',
    category: 'identity',
    deployments: ['on-premise', 'sovereign-cloud', 'saas'],
    features: [
      { title: 'Web SSO', description: 'Single sign-on across all your web applications.' },
      { title: 'Federated identity', description: 'SAML, OIDC, CAS — federate across organizations.' },
      { title: 'Role-based access', description: 'Granular RBAC down to application-level policies.' },
      { title: 'Directory integration', description: 'Plugs into OpenLDAP, Active Directory, Fusion Directory.' },
    ],
    stats: [
      { value: 'SAML', label: 'OIDC · CAS' },
      { value: '∞', label: 'Apps federated' },
      { value: 'RBAC', label: 'Granular' },
      { value: 'EU', label: 'Hosted option' },
    ],
    highlights: [
      'Web SSO across all applications',
      'SAML, OIDC, CAS federation',
      'Granular role-based access control',
      'Integrates with OpenLDAP and AD',
    ],
    pricingNote: 'Per-instance licensing. SaaS plan available. Contact for quote.',
  },
  {
    slug: 'openldap',
    name: 'OpenLDAP',
    tagline: 'Enterprise directory services',
    description:
      'Enterprise directory services built on OpenLDAP. Centralized identity store, replication, schema extensions. Managed by certified engineers or self-hosted.',
    icon: Users,
    accent: 'from-green-600 to-emerald-700',
    category: 'identity',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Centralized directory', description: 'Single source of truth for identities across your stack.' },
      { title: 'Multi-master replication', description: 'High-availability replication across datacenters.' },
      { title: 'Schema extensions', description: 'Custom schema extensions for your organization.' },
      { title: 'Fusion Directory UI', description: 'Web-based management interface for administrators.' },
    ],
    stats: [
      { value: '∞', label: 'Identities' },
      { value: 'HA', label: 'Replication' },
      { value: 'LDAPv3', label: 'Standard' },
      { value: 'EU', label: 'Hosted option' },
    ],
    highlights: [
      'Centralized identity store',
      'Multi-master replication',
      'Custom schema extensions',
      'Fusion Directory management UI',
    ],
    pricingNote: 'Per-instance licensing. Managed cloud plan available. Contact for quote.',
  },
  {
    slug: 'nextcloud',
    name: 'Nextcloud',
    tagline: 'Secure file sharing and collaboration',
    description:
      'Secure file sharing and collaboration platform. Files, calendars, contacts, tasks — self-hosted or managed European cloud. Integrates with Zimbra via the Nextcloud zimlet.',
    icon: Cloud,
    accent: 'from-blue-500 to-blue-800',
    category: 'collaboration',
    deployments: ['on-premise', 'sovereign-cloud', 'saas'],
    features: [
      { title: 'File sync & share', description: 'Desktop, mobile and web sync with end-to-end encryption.' },
      { title: 'Collabora Office', description: 'Real-time co-editing of documents, spreadsheets, presentations.' },
      { title: 'Calendar & contacts', description: 'CalDAV/CardDAV — syncs with Zimbra and mobile.' },
      { title: 'Zimbra integration', description: 'Native zimlet drops Nextcloud into Zimbra webmail.' },
    ],
    stats: [
      { value: 'E2E', label: 'Encryption' },
      { value: '∞', label: 'Storage' },
      { value: 'CalDAV', label: 'CardDAV' },
      { value: 'EU', label: 'Hosted option' },
    ],
    highlights: [
      'File sync & share with E2E encryption',
      'Collabora Office co-editing',
      'Calendar & contacts (CalDAV/CardDAV)',
      'Native Zimbra zimlet integration',
    ],
    pricingNote: 'Per-instance licensing. SaaS plan available. Contact for quote.',
  },
  {
    slug: 'mailgateway',
    name: 'Proxmox Mail Gateway',
    tagline: 'Advanced email protection',
    description:
      'Advanced email protection with Proxmox Mail Gateway. Spam filtering, virus scanning, DKIM/DMARC/SPF enforcement. Fronts Zimbra and any SMTP server.',
    icon: Mail,
    accent: 'from-rose-500 to-red-600',
    category: 'infrastructure',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Spam & virus filtering', description: 'Bayesian spam filter + ClamAV virus scanning.' },
      { title: 'DKIM/DMARC/SPF', description: 'Enforce email authentication policies at the gateway.' },
      { title: 'Quarantine management', description: 'Per-user quarantine with daily digest emails.' },
      { title: 'Zimbra integration', description: 'Fronts Zimbra — transparent to end users.' },
    ],
    stats: [
      { value: '99.9%', label: 'Spam caught' },
      { value: 'ClamAV', label: 'Virus scan' },
      { value: 'DKIM', label: 'DMARC · SPF' },
      { value: 'EU', label: 'Hosted option' },
    ],
    highlights: [
      'Spam & virus filtering',
      'DKIM/DMARC/SPF enforcement',
      'Per-user quarantine with digests',
      'Transparent Zimbra integration',
    ],
    pricingNote: 'Per-instance licensing. Managed cloud plan available. Contact for quote.',
  },
  {
    slug: 'adsync',
    name: 'AD Sync',
    tagline: 'Active Directory synchronization',
    description:
      'Bidirectional synchronization between Active Directory and OpenLDAP / Fusion Directory. Keep identities in sync across your directory infrastructure.',
    icon: GitMerge,
    accent: 'from-teal-500 to-cyan-700',
    category: 'identity',
    deployments: ['on-premise'],
    features: [
      { title: 'Bidirectional sync', description: 'Two-way sync between AD and OpenLDAP / Fusion Directory.' },
      { title: 'Scheduled reconciliation', description: 'Delta-only sync on schedule — minimal load on directories.' },
      { title: 'Security policies', description: 'Enforce access policies across synced directories.' },
      { title: 'Validation & launch', description: 'Full validation pass before production cutover.' },
    ],
    stats: [
      { value: '2-way', label: 'Sync' },
      { value: 'Delta', label: 'Reconciliation' },
      { value: 'AD', label: 'OpenLDAP' },
      { value: 'On-prem', label: 'Only' },
    ],
    highlights: [
      'Bidirectional AD ↔ OpenLDAP sync',
      'Scheduled delta reconciliation',
      'Cross-directory security policies',
      'Validation pass before cutover',
    ],
    pricingNote: 'Per-instance licensing. On-premise only. Contact for quote.',
  },
  {
    slug: 'ldap-zimbra-provisioning',
    name: 'LDAP → Zimbra Provisioning',
    tagline: 'Automatic Zimbra account provisioning from your LDAP directory',
    description:
      'Your directory becomes the single source of truth for Zimbra. New hires get a mailbox automatically, changes are synced, leavers are locked — from OpenLDAP, Active Directory or Fusion Directory, without manual admin work.',
    icon: UserCog,
    accent: 'from-teal-500 to-cyan-600',
    category: 'identity',
    badge: 'NEW',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Automatic account creation', description: 'Each new directory user gets a Zimbra mailbox with the right Class of Service.' },
      { title: 'Attribute sync', description: 'Name, title, phone, aliases and groups kept identical between LDAP and Zimbra.' },
      { title: 'Lifecycle management', description: 'Leavers are locked, archived or deleted according to your retention policy.' },
      { title: 'Group-based rules', description: 'LDAP groups map to COS, distribution lists and quotas.' },
      { title: 'Multi-domain', description: 'One directory can feed several Zimbra domains and tenants.' },
      { title: 'Audit log', description: 'Every create, update and lock recorded — reviewable and exportable.' },
    ],
    stats: [
      { value: 'Auto', label: 'Provisioning' },
      { value: 'LDAP', label: 'AD · Fusion' },
      { value: '0', label: 'Manual tasks' },
      { value: 'Audit', label: 'Full trail' },
    ],
    highlights: [
      'Mailboxes created automatically from LDAP',
      'Attributes, aliases and groups kept in sync',
      'Leavers locked according to your policy',
      'Works with OpenLDAP, Active Directory and Fusion Directory',
    ],
    pricingNote: 'Per-instance licensing. On-premise or managed. Contact for quote.',
  },
  {
    slug: 'zimbra-share-gateway',
    name: 'Zimbra Share Gateway',
    tagline: 'Shared mailboxes finally available everywhere, including on mobile',
    description:
      'The gateway that unlocks the full potential of Zimbra shared mailboxes: smartphone access over Exchange ActiveSync, signatures, filters and out-of-office managed inside the webmail, full mobile device control and an administration dashboard with complete audit.',
    icon: Share2,
    accent: 'from-blue-600 to-cyan-500',
    category: 'collaboration',
    badge: 'NEW',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Shared mailboxes on mobile', description: 'Every Exchange ActiveSync client opens shared mailboxes like its own.' },
      { title: 'Automatic setup', description: 'Devices configure themselves through Autodiscover.' },
      { title: 'Manage everything from the webmail', description: 'Signatures, out-of-office, filters, preferences and send-as in Zimbra Classic and Modern.' },
      { title: 'Mobile device control', description: 'Inventory, suspend, resume or remove devices, full sync history.' },
      { title: 'Fine-grained access policies', description: 'Time restrictions, IP filtering and automatic expiry.' },
      { title: 'Administration dashboard', description: 'Centralised share management, CSV bulk operations, real-time analytics.' },
      { title: 'Audit and compliance', description: '15+ event types logged, filterable and exportable.' },
      { title: 'Enterprise-grade security', description: 'No stored passwords, TLS, RBAC, rate limiting, OpenID Connect SSO.' },
      { title: 'Monitoring and alerts', description: 'Health monitoring with email and webhook alerts.' },
      { title: 'Lightweight, fast to deploy', description: 'Under 30 MB RAM, one-command install on Zimbra 8.8.15+.' },
    ],
    stats: [
      { value: '100%', label: 'EAS clients' },
      { value: '0', label: 'Client config' },
      { value: '<5 ms', label: 'Proxy overhead' },
      { value: '<30 MB', label: 'Memory' },
    ],
    highlights: [
      'Shared mailboxes available on every smartphone',
      'Signatures, filters and out-of-office managed from the webmail',
      'Full device control and access policies',
      'Complete audit, no password stored',
    ],
    pricingNote: 'Per Zimbra instance licensing. Deployment and support included. Contact for quote.',
  },
  {
    slug: 'fcshare',
    name: 'FcShare',
    tagline: 'Nextcloud file sharing protected by FranceConnect',
    description:
      'Share sensitive files from Nextcloud with a link only the intended recipient can open. The recipient proves their identity through FranceConnect, the French national identity service, and every step is recorded in an audit log.',
    icon: BadgeCheck,
    accent: 'from-blue-700 to-red-600',
    category: 'collaboration',
    badge: 'NEW',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Identity-verified sharing', description: 'Only the designated recipient can download the file after a FranceConnect login.' },
      { title: 'Email matching', description: 'The FranceConnect email must match the recipient declared when the link was created.' },
      { title: 'Native in Nextcloud Files', description: 'Create a FranceConnect share from the usual sharing sidebar in one click.' },
      { title: 'Real-time status', description: 'See whether each share is pending, verified or downloaded, with dates.' },
      { title: 'Audit dashboard', description: 'Links opened, logins, failures and downloads — exportable to CSV.' },
      { title: 'Sandbox to production', description: 'Mock, FranceConnect sandbox and production environments, plus a REST API.' },
    ],
    stats: [
      { value: 'FC', label: 'Verified identity' },
      { value: '100%', label: 'Traceable' },
      { value: 'CSV', label: 'Audit export' },
      { value: 'NC 28-30', label: 'Compatible' },
    ],
    highlights: [
      'Only the designated recipient can open the file',
      'Identity checked through FranceConnect',
      'Complete audit trail with CSV export',
      'Built into the Nextcloud Files sharing panel',
    ],
    pricingNote: 'Per-instance licensing. Installation and FranceConnect onboarding included. Contact for quote.',
  },
  {
    slug: 'infrastructure',
    name: 'Infrastructure Stack',
    tagline: 'Virtualization, network, remote access',
    description:
      'Enterprise infrastructure stack: Proxmox VE virtualization, OPNsense network security, Apache Guacamole remote access. Deployed and managed by certified engineers.',
    icon: Server,
    accent: 'from-slate-600 to-gray-800',
    category: 'infrastructure',
    deployments: ['on-premise', 'sovereign-cloud'],
    features: [
      { title: 'Proxmox VE', description: 'Enterprise virtualization — VMs, containers, high availability clusters.' },
      { title: 'OPNsense', description: 'Network security and firewall — IDS/IPS, VPN, traffic shaping.' },
      { title: 'Apache Guacamole', description: 'Secure remote access — RDP, VNC, SSH through the browser.' },
      { title: 'Certified deployment', description: 'Designed, deployed and hardened by certified engineers.' },
    ],
    stats: [
      { value: 'HA', label: 'Clusters' },
      { value: 'IDS/IPS', label: 'Firewall' },
      { value: 'Browser', label: 'Remote access' },
      { value: '24/7', label: 'Support' },
    ],
    highlights: [
      'Proxmox VE virtualization',
      'OPNsense network security',
      'Apache Guacamole remote access',
      'Certified engineer deployment',
    ],
    pricingNote: 'Project-based pricing. Scoped by infrastructure size. Contact for quote.',
  },
];

export const productCategories = [
  { id: 'ai', label: 'AI Solutions', icon: Sparkles },
  { id: 'collaboration', label: 'Collaboration', icon: Video },
  { id: 'identity', label: 'Identity & Access', icon: Shield },
  { id: 'infrastructure', label: 'Infrastructure', icon: Server },
  { id: 'platform', label: 'Platform', icon: Monitor },
  { id: 'migration', label: 'Migration', icon: ArrowRightLeft },
] as const;

export const deploymentModels = [
  {
    id: 'sovereign-cloud',
    name: 'Sovereign European Cloud',
    description: 'ilger.com runs the software for you in EU datacenters — fully managed, updated and monitored. All the sovereignty, none of the ops.',
    icon: Cloud,
    accent: 'from-cyan-500 to-blue-600',
    features: ['Fully managed by ilger.com', 'Hosted in EU datacenters', 'No CLOUD Act exposure', '24/7 monitoring · 99.9% SLA'],
  },
  {
    id: 'on-premise',
    name: 'On-Premise',
    description: 'The same software runs on your own servers — full control, air-gapped if you want. Two servers, one command.',
    icon: Server,
    accent: 'from-slate-500 to-blue-700',
    features: ['Full control of hardware', 'Air-gapped capable', 'No data leaves your network', 'Per-instance licensing'],
  },
  {
    id: 'standalone',
    name: 'Standalone Instance',
    description: 'No Zimbra? Run the software on its own — video, chat and on-premise AI as a self-contained suite on your servers.',
    icon: Monitor,
    accent: 'from-emerald-500 to-teal-600',
    features: ['Self-contained suite', 'No Zimbra required', 'Video, chat, AI included', 'Spin up in under a minute'],
  },
  {
    id: 'saas',
    name: 'SaaS',
    description: 'Some products available as a hosted SaaS — identity, storage, email. Pay per use, no infrastructure to manage.',
    icon: Network,
    accent: 'from-orange-500 to-red-600',
    features: ['Pay per use', 'No infrastructure to manage', 'Instant onboarding', 'Scalable by design'],
  },
] as const;

export function getProduct(slug: string): ProductMeta | undefined {
  return products.find((p) => p.slug === slug);
}
